import type { i18n as I18nInstance } from 'i18next';
import type { UserInfo } from '../types/auth';
import type {
  AppCurrencyCode,
  AppTheme,
  AppUiStyle,
  UserAppConfig,
} from '../types/userAppConfig';

const CURRENCY_CODES = new Set<string>([
  'CNY', 'HKD', 'TWD', 'USD', 'JPY', 'KRW', 'GBP', 'EUR', 'THB',
]);

const CHINESE_CURRENCY_TO_CODE: Record<string, AppCurrencyCode> = {
  人民币: 'CNY',
  港币: 'HKD',
  新台币: 'TWD',
  美元: 'USD',
  日元: 'JPY',
  韩元: 'KRW',
  英镑: 'GBP',
  欧元: 'EUR',
  泰铢: 'THB',
};

const CHINESE_LANGUAGE_TO_I18N: Record<string, string> = {
  中文简体: 'zh-CN',
  中文繁体: 'zh-HK',
  英语: 'en',
  日语: 'ja',
  韩语: 'ko',
};

export function normalizeLanguageCode(lang: string | undefined | null): string {
  if (!lang || typeof lang !== 'string') return 'zh-CN';
  const trimmed = lang.trim();
  if (CHINESE_LANGUAGE_TO_I18N[trimmed]) return CHINESE_LANGUAGE_TO_I18N[trimmed];
  if (trimmed.startsWith('zh-HK')) return 'zh-HK';
  if (trimmed.startsWith('zh')) return 'zh-CN';
  if (trimmed.startsWith('en')) return 'en';
  if (trimmed.startsWith('ja')) return 'ja';
  if (trimmed.startsWith('ko')) return 'ko';
  return trimmed;
}

export function mapLanguageToCurrency(lang: string): AppCurrencyCode {
  const code = normalizeLanguageCode(lang);
  switch (code) {
    case 'en':
      return 'USD';
    case 'zh-CN':
      return 'CNY';
    case 'zh-HK':
      return 'HKD';
    case 'ja':
      return 'JPY';
    case 'ko':
      return 'KRW';
    default:
      return 'USD';
  }
}

export function normalizeCurrencyCode(value: string | undefined | null): AppCurrencyCode | null {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim().toUpperCase();
  if (CURRENCY_CODES.has(trimmed)) return trimmed as AppCurrencyCode;
  if (CHINESE_CURRENCY_TO_CODE[value.trim()]) return CHINESE_CURRENCY_TO_CODE[value.trim()];
  return null;
}

export function parseExportLanguage(value: string | undefined | null): {
  primary: string;
  secondary?: string;
} {
  if (!value || typeof value !== 'string') {
    return { primary: 'zh-CN' };
  }
  const parts = value.split(';').map((p) => p.trim()).filter(Boolean);
  const primary = normalizeLanguageCode(parts[0]);
  const secondary = parts[1] ? normalizeLanguageCode(parts[1]) : undefined;
  if (secondary && secondary === primary) {
    return { primary };
  }
  return secondary ? { primary, secondary } : { primary };
}

export function formatExportLanguage(primary: string, secondary?: string | null): string {
  const p = normalizeLanguageCode(primary);
  if (!secondary) return p;
  const s = normalizeLanguageCode(secondary);
  if (!s || s === p) return p;
  return `${p};${s}`;
}

export function buildDefaultUserAppConfig(language: string): UserAppConfig {
  const lang = normalizeLanguageCode(language);
  const currency = mapLanguageToCurrency(lang);
  return {
    language: lang,
    currency,
    style: 'compact',
    theme: 'blue',
    exportLanguage: lang,
  };
}

export function parseUserAppConfig(raw: string | undefined | null): Partial<UserAppConfig> | null {
  if (!raw || typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Partial<UserAppConfig>;
  } catch {
    return null;
  }
}

export function resolveUserAppConfig(
  raw: string | undefined | null,
  fallbackLanguage: string,
): UserAppConfig {
  const defaults = buildDefaultUserAppConfig(fallbackLanguage);
  const partial = parseUserAppConfig(raw);
  if (!partial) return defaults;

  const language = partial.language
    ? normalizeLanguageCode(String(partial.language))
    : defaults.language;

  const currency =
    normalizeCurrencyCode(partial.currency as string) ??
    mapLanguageToCurrency(language);

  const exportLangRaw = partial.exportLanguage
    ? String(partial.exportLanguage)
    : language;
  const { primary: exportPrimary, secondary: exportSecondary } =
    parseExportLanguage(exportLangRaw);
  const exportLanguage = formatExportLanguage(exportPrimary, exportSecondary);

  const style: AppUiStyle =
    partial.style === 'loose' || partial.style === 'compact'
      ? partial.style
      : defaults.style;

  const theme: AppTheme =
    partial.theme === 'dark' ||
    partial.theme === 'blue' ||
    partial.theme === 'green' ||
    partial.theme === 'orange'
      ? partial.theme
      : defaults.theme;

  return {
    language,
    currency,
    style,
    theme,
    exportLanguage,
  };
}

export function serializeUserAppConfig(config: UserAppConfig): string {
  return JSON.stringify({
    language: config.language,
    currency: config.currency,
    style: config.style,
    theme: config.theme,
    exportLanguage: config.exportLanguage,
  });
}

export function mergeUserAppConfig(
  current: UserAppConfig,
  partial: Partial<UserAppConfig>,
): UserAppConfig {
  const merged: UserAppConfig = { ...current, ...partial };
  if (partial.language !== undefined) {
    merged.language = normalizeLanguageCode(partial.language);
  }
  if (partial.currency !== undefined) {
    const c = normalizeCurrencyCode(partial.currency as string);
    if (c) merged.currency = c;
  }
  if (partial.exportLanguage !== undefined) {
    const { primary, secondary } = parseExportLanguage(String(partial.exportLanguage));
    merged.exportLanguage = formatExportLanguage(primary, secondary);
  }
  if (partial.style !== undefined && (partial.style === 'compact' || partial.style === 'loose')) {
    merged.style = partial.style;
  }
  if (
    partial.theme !== undefined &&
    ['dark', 'blue', 'green', 'orange'].includes(partial.theme)
  ) {
    merged.theme = partial.theme;
  }
  return merged;
}

export interface ApplyUserAppConfigOptions {
  i18n: I18nInstance;
  setTheme?: (theme: AppTheme) => void;
}

export function applyUserAppConfig(
  config: UserAppConfig,
  options: ApplyUserAppConfigOptions,
): void {
  const { i18n, setTheme } = options;
  if (i18n.language !== config.language) {
    i18n.changeLanguage(config.language);
  }
  if (setTheme) {
    setTheme(config.theme);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-ui-density', config.style);
  }
}

export function resolveAndApplyUserAppConfig(
  user: UserInfo,
  fallbackLanguage: string,
  options: ApplyUserAppConfigOptions,
): UserAppConfig {
  const config = resolveUserAppConfig(user.userAppConfig, fallbackLanguage);
  applyUserAppConfig(config, options);
  return config;
}
